import { useState } from 'react';

import { Button } from '@gw/ui';
import { Info, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '@/stores/store';

export const SESSION_KEY = 'profile_nag_dismissed';

/**
 * ProfileCompletenessBanner
 *
 * GW-2325: Renders a dismissable session-scoped banner when the authenticated
 * user has not yet completed their profile (first name + last name missing).
 *
 * Show conditions:
 * - user.profileComplete is not true (from JWT custom:profileComplete claim)
 * - sessionStorage[SESSION_KEY] is not set
 *
 * Disappears immediately (reactively) when profileComplete transitions to true
 * after a successful profile save — no page reload required.
 */
export function ProfileCompletenessBanner() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(SESSION_KEY) === 'true');

  // Only render when profile is incomplete and not dismissed this session
  if (!user || user.profileComplete === true || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    sessionStorage.setItem(SESSION_KEY, 'true');
    setDismissed(true);
  };

  const handleComplete = () => {
    navigate('/settings');
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-muted flex items-center justify-between border-b px-6 py-2.5"
      data-testid="profile-completeness-banner"
    >
      <div className="flex items-center gap-3 text-sm">
        <Info className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          Add your name to complete your profile — it helps verify your identity on the platform.{' '}
          <button
            onClick={handleComplete}
            className="cursor-pointer font-semibold underline underline-offset-2 hover:no-underline"
            data-testid="profile-completeness-banner-link"
          >
            Complete Profile &rarr;
          </button>
        </span>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleDismiss}
        aria-label="Dismiss profile completion reminder"
        data-testid="profile-completeness-banner-dismiss"
        className="ml-4 shrink-0"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
